// @ts-nocheck
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import translations from '../i18n/translations';

const I18nContext = createContext(null);

/**
 * Dynamic i18n helpers:
 *  t('key')                         static
 *  t('welcome_user', { name: 'X' }) interpolation {{name}}
 *  td('status', 'available')        enum → t('status_available')
 *  tl(obj, 'name')                  bilingual DB field name / name_bn
 *  tOr(value, prefix)               try t(prefix_value) else humanize value
 */
export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem('rems_lang') || 'en');

    useEffect(() => {
        localStorage.setItem('rems_lang', lang);
        document.documentElement.lang = lang;
        document.documentElement.dir = 'ltr';
    }, [lang]);

    const dict = useMemo(() => translations[lang] || translations.en, [lang]);

    const t = useCallback(
        (key, vars) => {
            if (key == null || key === '') return '';
            let str = dict[key] ?? translations.en[key] ?? String(key);
            if (vars && typeof vars === 'object') {
                Object.keys(vars).forEach((k) => {
                    const val = vars[k] == null ? '' : String(vars[k]);
                    str = str.replace(new RegExp(`\\{\\{${k}\\}\\}`, 'g'), val);
                    str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), val);
                });
            }
            return str;
        },
        [dict]
    );

    /** Translate enum/status/type codes: td('status', 'available') → t('status_available') */
    const td = useCallback(
        (prefix, value) => {
            if (value == null || value === '') return '';
            const key = `${prefix}_${String(value)}`.replace(/[-\s]+/g, '_').toLowerCase();
            const hit = dict[key] ?? translations.en[key];
            if (hit) return hit;
            // fallback: try bare key
            const bare = dict[value] ?? translations.en[value];
            if (bare) return bare;
            // humanize snake_case
            return String(value).replace(/_/g, ' ');
        },
        [dict]
    );

    /** Bilingual object field from API (name / name_bn, label / label_bn) */
    const tl = useCallback(
        (obj, field = 'name') => {
            if (!obj) return '';
            if (lang === 'bn') {
                const bn = obj[`${field}_bn`] ?? obj[`${field}Bn`] ?? obj[`${field}BN`];
                if (bn) return bn;
            }
            return obj[field] ?? obj[`${field}_en`] ?? obj[`${field}En`] ?? '';
        },
        [lang]
    );

    /** Prefer t(key) if key exists, else return fallback string */
    const tOr = useCallback(
        (key, fallback) => {
            if (key && (dict[key] || translations.en[key])) return t(key);
            return fallback ?? key ?? '';
        },
        [dict, t]
    );

    const toggleLang = useCallback(() => setLang((l) => (l === 'en' ? 'bn' : 'en')), []);

    const value = useMemo(
        () => ({ lang, setLang, t, td, tl, tOr, toggleLang }),
        [lang, t, td, tl, tOr, toggleLang]
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => useContext(I18nContext);
